import {
  ensureJourneyDb,
  getById,
  insertRow,
  rowsFromIndex,
} from "../../db";
import { createNotification } from "../../db/schema";
import { readEmailFlags } from "./emailOutbox";

export function notifyUser({ user_id, type, payload }) {
  ensureJourneyDb();
  if (!user_id) return null;
  return insertRow(
    "notifications",
    createNotification({
      user_id,
      type,
      payload,
    })
  );
}

export function hydrateThread(thread) {
  const messages = rowsFromIndex("messages", "messagesByThreadId", thread.id).sort((a, b) =>
    String(a.timestamp).localeCompare(String(b.timestamp))
  );
  return {
    ...thread,
    messages: messages.map((message) => ({
      ...message,
      attachments: rowsFromIndex("attachments", "attachmentsByMessageId", message.id),
    })),
  };
}

export function hydrateTransaction(transaction) {
  const threads = rowsFromIndex("message_threads", "threadsByTransactionId", transaction.id).map(hydrateThread);
  return {
    ...transaction,
    broker: getById("users", transaction.broker_id),
    agent: getById("users", transaction.agent_id),
    client: getById("users", transaction.client_id),
    threads,
    exports: rowsFromIndex("exports", "exportsByTransactionId", transaction.id),
    archives: rowsFromIndex("archive_records", "archivesByTransactionId", transaction.id).map((row) => ({
      ...row,
      email: readEmailFlags(row.id),
    })),
  };
}

export function isArchivedThread(transaction, thread) {
  return Boolean(thread?.status === "archived" || (transaction && transaction.status !== "open"));
}

export function isOfficeTransaction(transaction) {
  return transaction?.client_role === "office";
}

export function unreadByChannel(transactions = [], userId) {
  return buildThreadItems(transactions, userId).reduce(
    (acc, item) => {
      const n = item.unread || 0;
      acc.messageUnread += n;
      if (item.clientRole === "office") acc.officeUnread += n;
      else acc.clientUnread += n;
      return acc;
    },
    { messageUnread: 0, clientUnread: 0, officeUnread: 0 }
  );
}

export function buildThreadItems(transactions = [], userId) {
  const items = [];
  transactions.forEach((transaction) => {
    const threads = transaction.threads || [];
    if (!threads.length) {
      items.push({
        key: `tx-${transaction.id}`,
        kind: "empty-tx",
        threadId: null,
        transactionId: transaction.id,
        title: transaction.property_address || "Conversation",
        clientName: transaction.client?.name || "",
        clientRole: transaction.client_role,
        agentName: transaction.agent?.name || "",
        lastPreview: "No thread yet",
        lastAt: transaction.created_at,
        unread: 0,
        isArchived: transaction.status !== "open",
        transactionStatus: transaction.status,
        threadStatus: null,
        transaction,
        thread: null,
      });
      return;
    }
    threads.forEach((thread) => {
      const messages = thread.messages || [];
      const last = messages[messages.length - 1];
      items.push({
        key: thread.id,
        kind: "thread",
        threadId: thread.id,
        transactionId: transaction.id,
        title: transaction.property_address || "Conversation",
        clientName: transaction.client?.name || "",
        clientRole: transaction.client_role,
        agentName: transaction.agent?.name || "",
        lastPreview: last?.message_text || "No messages yet",
        lastAt: last?.timestamp || thread.created_at,
        unread: messages.filter((message) => message.receiver_id === userId && !message.read_status).length,
        isArchived: isArchivedThread(transaction, thread),
        transactionStatus: transaction.status,
        threadStatus: thread.status,
        transaction,
        thread,
      });
    });
  });
  return items.sort((a, b) => String(b.lastAt || "").localeCompare(String(a.lastAt || "")));
}

export function checklistSnapshot(transaction) {
  if (transaction.client_role === "prospect") {
    return { type: "prospect", notes: null };
  }
  const client = getById("users", transaction.client_id);
  if (transaction.client_role === "seller") {
    try {
      const list = JSON.parse(localStorage.getItem("dh_sellers") || "[]");
      const current = JSON.parse(localStorage.getItem("dh_seller") || "null");
      const match =
        list.find((item) => item.id === transaction.client_id) ||
        list.find((item) => client?.email && item.email?.toLowerCase() === client.email.toLowerCase()) ||
        current;
      return { type: "seller", portfolio: match?.portfolio || null };
    } catch {
      return { type: "seller", portfolio: null };
    }
  }
  try {
    const buyers = JSON.parse(localStorage.getItem("dh_buyers") || "[]");
    const buyer =
      buyers.find((item) => item.id === transaction.client_id) ||
      buyers.find((item) => client?.email && item.email?.toLowerCase() === client.email.toLowerCase());
    return { type: "buyer", preferences: buyer?.preferences || null };
  } catch {
    return { type: "buyer", preferences: null };
  }
}
